import { useEffect, useMemo, useState } from 'react';
import {
  accessibleLessons, addVocabulary, courses, homework, me, progress, reviewVocabulary, submitHomework, vocabulary, vocabularyBank,
  type Course, type HomeworkItem, type LessonSummary, type ProgressItem, type VocabularyBankItem, type VocabularyItem,
} from '../lib/api';
import StudentLesson from './StudentLesson';
import AudioPlayer from '../components/AudioPlayer';
import StudentNavigation, { type StudentView } from './StudentNavigation';
import { StudentStatGrid, StudentStatus } from './StudentPrimitives';

type View = StudentView;

const cyrillic = /[А-Яа-яЁёІіЇїЄєҐґ]/;
function displayLessonTitle(lesson: LessonSummary) {
  if (!cyrillic.test(lesson.title)) return lesson.title;
  return /(^|-)de(-|$)/i.test(lesson.course_id) ? `Deutsch · Lektion ${lesson.lesson_number}` : `English · Lesson ${lesson.lesson_number}`;
}

interface Props { onOpenLesson?: () => void; }

export default function StudentDashboard({ onOpenLesson }: Props) {
  const [view, setView] = useState<View>('dashboard');
  const [courseList, setCourseList] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [lessonList, setLessonList] = useState<(LessonSummary & { unlocked: number })[]>([]);
  const [selectedLesson, setSelectedLesson] = useState<LessonSummary | null>(null);
  const [error, setError] = useState('');
  const [homeworkList, setHomeworkList] = useState<HomeworkItem[]>([]);
  const [vocabularyList, setVocabularyList] = useState<VocabularyItem[]>([]);
  const [vocabularyFilter, setVocabularyFilter] = useState<'all' | 'new' | 'review' | 'mastered'>('all');
  const [vocabularySearch, setVocabularySearch] = useState('');
  const [lessonWords, setLessonWords] = useState<VocabularyBankItem[]>([]);
  const [submissionFor, setSubmissionFor] = useState<string | null>(null);
  const [submissionText, setSubmissionText] = useState('');
  const [submissionMessage, setSubmissionMessage] = useState('');
  const [homeworkBusy, setHomeworkBusy] = useState(false);
  const [vocabularyMessage, setVocabularyMessage] = useState('');
  const [progressList, setProgressList] = useState<ProgressItem[]>([]);
  const [courseSearch, setCourseSearch] = useState('');
  const [courseUnitFilter, setCourseUnitFilter] = useState<number | 'all'>('all');
  const [revisionRevealed, setRevisionRevealed] = useState(false);
  const [revisionSessionDone, setRevisionSessionDone] = useState(0);

  useEffect(() => {
    courses().then((data) => { setCourseList(data); if (data[0]) setSelectedCourse(data[0].id); }).catch((e) => setError(e instanceof Error ? e.message : 'Could not load courses.'));
    me().then((actor) => {
      homework(actor.id).then(setHomeworkList).catch(() => {});
      vocabulary(actor.id).then(setVocabularyList).catch(() => {});
      progress(actor.id).then(setProgressList).catch(() => {});
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedCourse) return;
    accessibleLessons(selectedCourse).then(setLessonList).catch((e) => setError(e instanceof Error ? e.message : 'Could not load lessons.'));
  }, [selectedCourse]);

  useEffect(() => {
    if (!selectedLesson) return;
    window.scrollTo(0, 0);
  }, [selectedLesson]);

  const recommendedLesson = useMemo(() => {
    const unlocked = lessonList.filter((lesson) => lesson.unlocked === 1);
    return unlocked.find((lesson) => progressList.some((item) => item.lesson_id === lesson.id && item.status === 'in_progress'))
      || unlocked.find((lesson) => !progressList.some((item) => item.lesson_id === lesson.id && item.status === 'completed'))
      || unlocked[0] || null;
  }, [lessonList, progressList]);

  const curriculumLessons = useMemo(() => {
    const q = courseSearch.trim().toLowerCase();
    return lessonList.filter((lesson) => {
      const matchesUnit = courseUnitFilter === 'all' || lesson.unit_number === courseUnitFilter;
      const goals = (() => { try { const parsed = JSON.parse(lesson.goals_json); return Array.isArray(parsed) ? parsed.join(' ') : String(parsed || ''); } catch { return lesson.goals_json || ''; } })();
      const matchesSearch = !q || lesson.title.toLowerCase().includes(q) || goals.toLowerCase().includes(q);
      return matchesUnit && matchesSearch;
    });
  }, [lessonList, courseSearch, courseUnitFilter]);

  const groupedLessons = useMemo(() => {
    const groups = new Map<number, typeof lessonList>();
    curriculumLessons.forEach((lesson) => {
      if (!groups.has(lesson.unit_number)) groups.set(lesson.unit_number, []);
      groups.get(lesson.unit_number)!.push(lesson);
    });
    return [...groups.entries()].sort((a, b) => a[0] - b[0]);
  }, [curriculumLessons]);

  const dueNow = (item: VocabularyItem) => !item.due_at || new Date(item.due_at).getTime() <= Date.now();
  const filteredVocabulary = useMemo(() => {
    const q = vocabularySearch.trim().toLowerCase();
    return vocabularyList.filter((item) => {
      const matchesSearch = !q || item.word.toLowerCase().includes(q) || item.translation.toLowerCase().includes(q);
      const matchesFilter =
        vocabularyFilter === 'all' ||
        (vocabularyFilter === 'new' && item.review_state === 'new') ||
        (vocabularyFilter === 'mastered' && item.review_state === 'mastered') ||
        (vocabularyFilter === 'review' && item.review_state !== 'mastered' && dueNow(item));
      return matchesSearch && matchesFilter;
    });
  }, [vocabularyList, vocabularyFilter, vocabularySearch]);

  const nav = <StudentNavigation view={view} onChange={setView} />;

  const refreshHomework = async () => {
    const actor = await me();
    setHomeworkList(await homework(actor.id));
  };

  const refreshVocabulary = async () => {
    const actor = await me();
    setVocabularyList(await vocabulary(actor.id));
  };

  const refreshLearningState = async () => {
    const actor = await me();
    const [nextProgress, nextVocabulary] = await Promise.all([progress(actor.id), vocabulary(actor.id)]);
    setProgressList(nextProgress);
    setVocabularyList(nextVocabulary);
  };

  const openVocabulary = async () => {
    setView('vocabulary');
    setVocabularyMessage('');
    const nextLesson = lessonList.find((x) => x.unlocked === 1);
    if (nextLesson) {
      try { setLessonWords(await vocabularyBank(nextLesson.id)); } catch { setLessonWords([]); }
    }
  };

  if (selectedLesson) return <StudentLesson lesson={selectedLesson} onBack={() => { setSelectedLesson(null); refreshLearningState().catch(() => {}); }} />;

  if (view === 'course') return <section className="student-page">{nav}
    <div className="curriculum-hero">
      <div>
        <p className="eyebrow">COURSE LIBRARY · {courseList.find((x) => x.id === selectedCourse)?.language?.toUpperCase() || 'LANGUAGE'}</p>
        <h1>My learning path</h1>
        <p>Everything is organised as <b>level → unit → lesson → learning blocks</b>. Follow the sequence or return to any lesson your teacher has opened.</p><div className="course-switch-inline"><label>Course<select value={selectedCourse} onChange={(e) => { setSelectedCourse(e.target.value); setCourseUnitFilter("all"); setCourseSearch(""); }}>{courseList.map((item) => <option key={item.id} value={item.id}>{item.language.toUpperCase()} · {item.level}</option>)}</select></label></div>
      </div>
      <div className="curriculum-hero-stat"><strong>{lessonList.length}</strong><span>lessons in this course</span></div>
    </div>

    <section className="curriculum-controls panel">
      <label><span>Find a lesson</span><input value={courseSearch} onChange={(e) => setCourseSearch(e.target.value)} placeholder="Alphabet, family, grammar…" /></label>
      <label><span>Unit</span><select value={courseUnitFilter} onChange={(e) => setCourseUnitFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}><option value="all">All units</option>{[...new Set(lessonList.map((l) => l.unit_number))].sort((a,b)=>a-b).map((unit) => <option key={unit} value={unit}>Unit {unit}</option>)}</select></label>
      <button className="secondary-action" onClick={() => { setCourseSearch(''); setCourseUnitFilter('all'); }}>Reset</button>
    </section>

    <StudentStatGrid items={[
      { label: 'Open lessons', value: lessonList.filter((l) => l.unlocked).length, tone: 'cyan' },
      { label: 'Completed', value: lessonList.filter((l) => progressList.some((p) => p.lesson_id === l.id && p.status === 'completed')).length, tone: 'green' },
      { label: 'Learning blocks', value: lessonList.reduce((sum,l) => sum + Number(l.block_count || 0), 0), tone: 'purple' },
      { label: 'In progress', value: lessonList.filter((l) => progressList.some((p) => p.lesson_id === l.id && p.status === 'in_progress')).length, tone: 'neutral' },
    ]} />

    <div className="curriculum-units">
      {groupedLessons.map(([unit, unitLessons]) => {
        const unitCompleted = unitLessons.filter((lesson) => progressList.some((p) => p.lesson_id === lesson.id && p.status === 'completed')).length;
        const unitOpen = unitLessons.filter((lesson) => lesson.unlocked).length;
        return <section className="curriculum-unit" key={unit}>
          <div className="curriculum-unit-head">
            <div><span>UNIT {unit}</span><h2>{unitLessons[0]?.title ? 'Learning sequence' : 'Unit ' + unit}</h2><p>{unitOpen} of {unitLessons.length} lessons open · {unitCompleted} completed</p></div>
            <div className="unit-progress-ring"><strong>{unitLessons.length ? Math.round(unitCompleted / unitLessons.length * 100) : 0}%</strong><small>complete</small></div>
          </div>
          <div className="curriculum-lesson-grid">
            {unitLessons.map((lesson) => {
              const done = progressList.some((p) => p.lesson_id === lesson.id && p.status === 'completed');
              const active = progressList.some((p) => p.lesson_id === lesson.id && p.status === 'in_progress');
              let goals = '';
              try { const parsed = JSON.parse(lesson.goals_json); goals = Array.isArray(parsed) ? parsed.slice(0,2).join(' · ') : String(parsed || ''); } catch { goals = lesson.goals_json || ''; }
              return <article className={'curriculum-lesson-card ' + (!lesson.unlocked ? 'locked ' : '') + (done ? 'done ' : active ? 'active' : '')} key={lesson.id}>
                <div className="curriculum-lesson-top"><span>LESSON {lesson.lesson_number}</span><StudentStatus status={done ? 'success' : active ? 'active' : lesson.unlocked ? 'neutral' : 'locked'} label={done ? 'Complete' : active ? 'In progress' : lesson.unlocked ? 'Open' : 'Locked'} /></div>
                <h3>{lesson.title}</h3>
                {goals && <p>{goals}</p>}
                <div className="curriculum-lesson-meta"><span>{Number(lesson.block_count || 0)} blocks</span><span>{lesson.unlocked ? 'Teacher unlocked' : 'Waiting for teacher'}</span></div>
                {lesson.unlocked ? <button onClick={() => { setSelectedLesson(lesson); onOpenLesson?.(); }}>{active ? 'Continue lesson →' : done ? 'Review lesson →' : 'Start lesson →'}</button> : <span className="curriculum-lock">🔒 Locked</span>}
              </article>;
            })}
          </div>
        </section>;
      })}
      {!groupedLessons.length && <article className="panel curriculum-empty"><b>No lessons match this view.</b><p>Try another search or switch back to all units.</p></article>}
    </div>
  </section>;

  if (view === 'vocabulary') return <section className="student-page">{nav}
    <p className="eyebrow">VOCABULARY LAB</p><h1>Your word library</h1>
    <StudentStatGrid items={[
      { label: 'Total words', value: vocabularyList.length, tone: 'cyan' },
      { label: 'New', value: vocabularyList.filter((x) => x.review_state === 'new').length, tone: 'purple' },
      { label: 'Ready to review', value: vocabularyList.filter((x) => x.review_state !== 'mastered' && dueNow(x)).length, tone: 'neutral' },
      { label: 'Mastered', value: vocabularyList.filter((x) => x.review_state === 'mastered').length, tone: 'green' },
    ]} />
    {vocabularyMessage && <p className="form-success">{vocabularyMessage}</p>}
    <div className="vocab-toolbar">
      <input placeholder={`Search ${courseList.find((x) => x.id === selectedCourse)?.language === "en" ? "English" : "German"} or translation…`} value={vocabularySearch} onChange={(e) => setVocabularySearch(e.target.value)} />
      <div className="vocab-filters">{(['all','new','review','mastered'] as const).map((filter) => <button key={filter} className={vocabularyFilter === filter ? 'active' : ''} onClick={() => setVocabularyFilter(filter)}>{filter}</button>)}</div>
    </div>
    {lessonWords.length > 0 && <article className="vocab-collection">
      <div><span className="eyebrow">NEXT LESSON WORDS</span><h2>Build your library from the lesson</h2><p>Save only the words you want to keep. They will return here for spaced review.</p></div>
      <div className="word-bank">{lessonWords.map((word) => {
        const already = vocabularyList.some((item) => item.word.toLowerCase() === word.word.toLowerCase() && item.language === word.language);
        return <div className="word-bank-row" key={word.id}><div><b>{word.word}</b><span>{word.translation}</span><small>{word.example}</small></div><button disabled={already} onClick={async () => {
          try {
            await addVocabulary({ word:word.word, translation:word.translation, language:word.language, lesson_id:word.lesson_id, level:word.level, topic:word.topic, example:word.example, pronunciation:word.pronunciation });
            await refreshVocabulary();
            setVocabularyMessage('Added “' + word.word + '” to your library.');
          } catch (e) { setVocabularyMessage(e instanceof Error ? e.message : 'Could not add word.'); }
        }}>{already ? 'Saved' : 'Save word'}</button></div>;
      })}</div>
    </article>}
    {!filteredVocabulary.length ? <article className="panel"><b>No words in this view.</b><p>Save a few lesson words and they will appear here.</p></article> :
      <div className="vocab-grid">{filteredVocabulary.map((item) => <article className="vocab-card" key={item.id}>
        <div className="vocab-card-top"><span>{item.language.toUpperCase()} · {item.review_state}</span>{item.interval_days > 0 && <small>{item.interval_days}d interval</small>}</div>
        <h2>{item.word}</h2><strong>{item.translation}</strong>
        {item.pronunciation && <p className="pronunciation">/{item.pronunciation}/</p>}
        {item.example && <p className="example">{item.example}</p>}
        {item.review_state !== 'mastered' && <div className="review-actions">
          <button onClick={async () => { await reviewVocabulary(item.id, 'again'); await refreshVocabulary(); }}>Again</button>
          <button onClick={async () => { await reviewVocabulary(item.id, 'good'); await refreshVocabulary(); }}>Good</button>
          <button onClick={async () => { await reviewVocabulary(item.id, 'easy'); await refreshVocabulary(); }}>Easy</button>
        </div>}
      </article>)}</div>}
  </section>;

  if (view === 'homework') {
    const pendingCount = homeworkList.filter((item) => !item.submission_id).length;
    const reviewedCount = homeworkList.filter((item) => item.submission_status === 'reviewed').length;
    return <section className="student-page">{nav}
      <p className="eyebrow">HOMEWORK</p><h1>Homework</h1>
      <StudentStatGrid items={[
        { label: 'To do', value: pendingCount, tone: 'cyan' },
        { label: 'Assigned', value: homeworkList.length, tone: 'purple' },
        { label: 'Reviewed', value: reviewedCount, tone: 'green' },
      ]} />
      {submissionMessage && <p className="form-success">{submissionMessage}</p>}
      {!homeworkList.length ? <article className="panel"><b>No homework yet.</b><p>Your teacher's 10–15 minute tasks will appear here.</p></article> :
        <div className="homework-list">{homeworkList.map((item) => {
          const submitted = !!item.submission_id;
          const reviewed = item.submission_status === 'reviewed';
          return <article className={'homework-card ' + (reviewed ? 'reviewed' : submitted ? 'submitted' : '')} key={item.id}>
            <div className="homework-card-head">
              <div><span className="homework-kind">{item.kind.toUpperCase()}</span><h2>{item.title}</h2></div>
              <StudentStatus status={reviewed ? 'success' : submitted ? 'active' : 'neutral'} label={reviewed ? 'Reviewed' : submitted ? 'Submitted' : 'To do'} />
            </div>
            <p className="homework-instructions">{item.instructions}</p>
            {item.due_at && <p className="homework-due">Due: {new Date(item.due_at).toLocaleString()}</p>}
            {submitted && <div className="submission-status"><b>Your latest submission</b><span>{item.submitted_at ? new Date(item.submitted_at).toLocaleString() : 'Submitted'}</span></div>}
            {item.feedback && <div className="teacher-feedback"><span>TEACHER FEEDBACK</span><p>{item.feedback}</p></div>}
            {submissionFor === item.id && <div className="admin-form homework-editor">
              <textarea rows={6} placeholder="Write your answer or reflection here…" value={submissionText} onChange={(e) => setSubmissionText(e.target.value)} />
              <div className="homework-editor-actions">
                <button className="secondary-action" onClick={() => { setSubmissionFor(null); setSubmissionText(''); }}>Cancel</button>
                <button disabled={!submissionText.trim() || homeworkBusy} onClick={async () => {
                  try {
                    setHomeworkBusy(true);
                    await submitHomework({ homework_id:item.id, answer:submissionText.trim() });
                    await refreshHomework();
                    setSubmissionMessage('Submission sent to your teacher.');
                    setSubmissionFor(null);
                    setSubmissionText('');
                  } catch (e) { setSubmissionMessage(e instanceof Error ? e.message : 'Could not submit homework.'); }
                  finally { setHomeworkBusy(false); }
                }}>{homeworkBusy ? 'Sending…' : submitted ? 'Submit again' : 'Submit homework'}</button>
              </div>
            </div>}
            <div className="homework-card-actions">
              <button onClick={() => { setSubmissionFor(submissionFor === item.id ? null : item.id); setSubmissionText(''); setSubmissionMessage(''); }}>
                {submitted ? 'Submit again' : 'Start homework'}
              </button>
            </div>
          </article>;
        })}</div>}
    </section>;
  }

  if (view === 'progress') return <section className="student-page">{nav}
    <p className="eyebrow">LEARNING PULSE</p><h1>Your progress</h1>
    <p className="intro">A clear view of what you have completed, what needs another pass, and how much learning time you have logged.</p>
    <StudentStatGrid items={[
      { label: 'Lessons completed', value: new Set(progressList.filter((x) => x.status === 'completed').map((x) => x.lesson_id)).size, tone: 'green' },
      { label: 'Blocks completed', value: progressList.filter((x) => x.block_id && x.status === 'completed').length, tone: 'cyan' },
      { label: 'Average scored work', value: (progressList.filter((x) => x.score != null).length ? Math.round(progressList.filter((x) => x.score != null).reduce((s,x)=>s+(x.score||0),0)/progressList.filter((x)=>x.score!=null).length) : 0) + '%', tone: 'purple' },
      { label: 'Minutes logged', value: Math.round(progressList.reduce((s,x)=>s+x.seconds_spent,0)/60), tone: 'neutral' },
    ]} />
    <div className="progress-dashboard-grid">
      <article className="panel">
        <span className="eyebrow">COURSE MAP</span><h2>{courseList.find((x)=>x.id===selectedCourse)?.title || 'Your course'}</h2>
        {groupedLessons.map(([unit, unitLessons]) => <div className="progress-unit" key={unit}>
          <div className="progress-unit-head"><b>Unit {unit}</b><span>{unitLessons.filter((l)=>progressList.some((p)=>p.lesson_id===l.id&&p.status==='completed')).length}/{unitLessons.length}</span></div>
          {unitLessons.map((l) => { const done=progressList.some((p)=>p.lesson_id===l.id&&p.status==='completed'); const active=progressList.some((p)=>p.lesson_id===l.id&&p.status==='in_progress'); return <button className={"progress-lesson "+(done?'done ':active?'active ':'')} key={l.id} onClick={()=>l.unlocked&&setSelectedLesson(l)}><span>{done?'✓':active?'•':'○'}</span><div><b>{displayLessonTitle(l)}</b><small>{done?'Completed':active?'In progress':l.unlocked?'Ready to learn':'Locked'}</small></div></button>; })}
        </div>)}
      </article>
      <article className="panel">
        <span className="eyebrow">RECENT ACTIVITY</span><h2>Latest learning</h2>
        {!progressList.length ? <p>No activity yet. Open your next lesson to start.</p> : <div className="activity-list">{progressList.slice(0,8).map((p)=><div className="activity-row" key={p.id}><span>{p.status==='completed'?'✓':'•'}</span><div><b>{lessonList.find((l)=>l.id===p.lesson_id)?.title || p.lesson_id}</b><small>{p.block_id?'Exercise / block':'Lesson'} · {new Date(p.updated_at).toLocaleString()} {p.score != null ? ' · '+Math.round(p.score)+'%' : ''}</small></div></div>)}</div>}
      </article>
    </div>
  </section>;

  if (view === 'revision') {
    const dueWords = vocabularyList.filter((x) => x.review_state !== 'mastered' && dueNow(x));
    const sessionWords = dueWords.slice(0, 5);
    const currentWord = sessionWords[0];
    const sessionTotal = Math.min(5, dueWords.length);
    const sessionPosition = Math.min(revisionSessionDone + 1, sessionTotal);
    const finishReview = async (rating: 'again' | 'good' | 'easy') => {
      if (!currentWord) return;
      await reviewVocabulary(currentWord.id, rating);
      await refreshVocabulary();
      setRevisionSessionDone((value) => value + 1);
      setRevisionRevealed(false);
    };
    return <section className="student-page">{nav}
      <p className="eyebrow">REVISION LAB</p><h1>Revision lab</h1>
      <div className="revision-overview"><div><b>5-word focus session</b><p>Recall the word before revealing the meaning. Listen once, say it aloud, then rate how easy the recall was.</p></div><div className="revision-rule"><span>{dueWords.length}</span><small>words due now</small></div></div>
      {vocabularyMessage && <p className="form-success">{vocabularyMessage}</p>}
      {!dueWords.length ? <article className="panel"><b>Nothing is due right now.</b><p>Save useful lesson words or come back when the next review interval arrives.</p><button className="inline-action" onClick={openVocabulary}>Open vocabulary</button></article> :
        <article className="revision-session panel">
          <div className="revision-session-head"><span>SESSION · {sessionPosition} / {sessionTotal}</span><strong>{currentWord?.language.toUpperCase()}</strong></div>
          {currentWord && <div className="revision-focus-card">
            <small>LOOK → RECALL → SPEAK</small><h2>{currentWord.word}</h2>
            {currentWord.pronunciation && <p className="pronunciation">/{currentWord.pronunciation}/</p>}
            <AudioPlayer text={currentWord.word + (currentWord.example ? '. ' + currentWord.example : '')} language={currentWord.language === 'de' ? 'de' : 'en'} label="Hear natural voice" />
            <button className="revision-reveal" type="button" onClick={() => setRevisionRevealed((value) => !value)}>{revisionRevealed ? 'Hide meaning' : 'Reveal meaning'}</button>
            {revisionRevealed && <div className="revision-answer"><strong>{currentWord.translation}</strong>{currentWord.example && <span>{currentWord.example}</span>}</div>}
            <div className="review-actions revision-session-actions" role="group" aria-label="Rate recall"><button type="button" onClick={() => finishReview('again')}>Again<span>Review sooner</span></button><button type="button" onClick={() => finishReview('good')}>Good<span>Keep pace</span></button><button type="button" onClick={() => finishReview('easy')}>Easy<span>Longer interval</span></button></div>
          </div>}
          <p className="revision-session-tip">Tip: if you needed the reveal, choose <b>Again</b>. Comfortable recall is <b>Good</b>; immediate recall is <b>Easy</b>.</p>
        </article>}
      {dueWords.length > 5 && <p className="intro">Your focused session uses the first 5 due words. {dueWords.length - 5} more remain in the full review queue.</p>}
    </section>;
  }

  const current = recommendedLesson;
  const completedLessons = new Set(progressList.filter((item) => item.status === 'completed').map((item) => item.lesson_id));
  const inProgressLessons = new Set(progressList.filter((item) => item.status === 'in_progress').map((item) => item.lesson_id));
  return <section className="student-page">{nav}
    <p className="eyebrow">STUDENT DASHBOARD</p><h1>Guten Tag.</h1>
    <p className="intro">Your learning path is connected to your teacher. Continue where you stopped, review your words, or open the next lesson.</p>
    {error && <p className="form-error">{error}</p>}
    {current ? <article className="current-lesson"><div><span>{inProgressLessons.has(current.id) ? 'Continue learning' : completedLessons.has(current.id) ? 'Next lesson' : 'Recommended next'}</span><h2>{displayLessonTitle(current)}</h2><p>{inProgressLessons.has(current.id) ? 'Pick up from your lesson map and continue with the next unfinished block.' : 'Open the lesson and work through the interactive blocks at your own pace.'}</p></div><button onClick={() => setSelectedLesson(current)}>{inProgressLessons.has(current.id) ? 'Continue' : 'Start lesson'}</button></article> : <article className="panel"><b>No lessons unlocked yet.</b><p>Ask your teacher to unlock the first lesson for your pair.</p></article>}
    <div className="today-grid">
      <article className="panel"><span className="eyebrow">TODAY</span><h2>Learning checklist</h2><div className="today-checks"><button onClick={() => setView('vocabulary')}><span>01</span><div><b>Vocabulary</b><small>{vocabularyList.filter((x) => x.review_state !== 'mastered' && dueNow(x)).length} words ready for review</small></div><strong>→</strong></button><button onClick={() => setView('course')}><span>02</span><div><b>Lesson practice</b><small>{inProgressLessons.size ? 'Continue an unfinished lesson' : 'Open your recommended lesson'}</small></div><strong>→</strong></button><button onClick={() => setView('homework')}><span>03</span><div><b>Homework</b><small>{homeworkList.filter((x) => !x.submission_id).length} tasks waiting</small></div><strong>→</strong></button><button onClick={() => setView('progress')}><span>04</span><div><b>Progress</b><small>See completed blocks and recent activity</small></div><strong>→</strong></button></div></article>
      <article className="panel"><span className="eyebrow">AT A GLANCE</span><h2>Your learning</h2><div className="metric-grid compact"><article><b>{completedLessons.size}</b><span>lessons completed</span></article><article><b>{progressList.filter((x) => x.block_id && x.status === 'completed').length}</b><span>blocks completed</span></article><article><b>{vocabularyList.length}</b><span>saved words</span></article><article><b>{homeworkList.filter((x) => !x.submission_id).length}</b><span>homework to do</span></article></div></article>
    </div>
  </section>;
}
